import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { OrderHubLineCmmCell } from '@/app/(client)/orders-hub/-components/OrderHubLineCmmCell';
import { OrderHubLineTracingCell } from '@/app/(client)/orders-hub/-components/OrderHubLineTracingCell';
import { OrderHubOverviewTab } from '@/app/(client)/orders-hub/-components/OrderHubOverviewTab';
import { SendToCmmDialog } from '@/app/(client)/orders-hub/-components/SendToCmmDialog';
import { SendToTracingDialog } from '@/app/(client)/orders-hub/-components/SendToTracingDialog';
import { useCmmSendFlow } from '@/app/(client)/orders-hub/-hooks/useCmmSendFlow';
import {
  referencedCompaniesQueryOptions,
  useOrderHubCompanyOptions,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubCompanyOptions';
import { useOrderHubCounterpartyProfile } from '@/app/(client)/orders-hub/-hooks/useOrderHubCounterpartyProfile';
import { useOrderHubLinesTable } from '@/app/(client)/orders-hub/-hooks/useOrderHubLinesTable';
import {
  orderHubDetailQueryOptions,
  useOrderHubOrderDetail,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderDetail';
import { orderHubOrderLinesQueryOptions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderLinesList';
import { useOrderHubOverview } from '@/app/(client)/orders-hub/-hooks/useOrderHubOverview';
import { useOrdersReadiness } from '@/app/(client)/orders-hub/-hooks/useOrdersReadiness';
import { useSendToTracingFlow } from '@/app/(client)/orders-hub/-hooks/useSendToTracingFlow';
import {
  isSupplierWithoutActiveUser,
  useSupplierUserStats,
} from '@/app/(client)/orders-hub/-hooks/useSupplierUserStats';
import { useUpdateOrderHubOrder } from '@/app/(client)/orders-hub/-hooks/useUpdateOrderHubOrder';
import { useCompanyUsersQuery } from '@/hooks/useCompanyUsersQuery';
import { useTranslator } from '@/hooks/useTranslator';
import { getCompanyId } from '@/lib/utils/apiFetch';
import { queryClient } from '@/lib/utils/queryClient';

interface OverviewSearch {
  /** Present only when the Order Lines rail node is selected. */
  section?: 'lines';
}

export const Route = createFileRoute(
  '/(client)/orders-hub/(pages)/(detail)/$id/overview',
)({
  validateSearch: (search: Record<string, unknown>): OverviewSearch =>
    search.section === 'lines' ? { section: 'lines' } : {},
  loader: async ({ params }) => {
    const [order] = await Promise.all([
      queryClient.ensureQueryData(orderHubDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(orderHubOrderLinesQueryOptions(params.id)),
    ]);
    // Warm only the ≤5 referenced companies (id-scoped) so their names paint
    // without a flash — no eager full-network fetch on open.
    const ids = [
      order.buyerCompanyId,
      order.supplierCompanyId,
      order.receiverCompanyId,
      order.vendorCompanyId,
      order.factory,
    ].filter((id): id is string => !!id);
    if (ids.length > 0) {
      await queryClient.ensureQueryData(referencedCompaniesQueryOptions(ids));
    }
  },
  component: OrderHubOverviewRoute,
});

function OrderHubOverviewRoute() {
  const { id } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = useNavigate();

  const [picklistEnabled, setPicklistEnabled] = useState(false);

  const { data: order } = useOrderHubOrderDetail(id);
  const { options, isPicklistLoading } = useOrderHubCompanyOptions(order, {
    picklistEnabled,
  });
  const { patch } = useUpdateOrderHubOrder(id);
  const supplierProfile = useOrderHubCounterpartyProfile(
    order?.supplierCompanyId,
  );
  const buyerProfile = useOrderHubCounterpartyProfile(order?.buyerCompanyId);
  const receiverProfile = useOrderHubCounterpartyProfile(
    order?.receiverCompanyId,
  );
  const linesTable = useOrderHubLinesTable(id);
  const sendFlow = useSendToTracingFlow();
  const cmmFlow = useCmmSendFlow();
  // Same cache entry as the order page header.
  const { data: sendReadiness } = useOrdersReadiness([id], {
    includeLines: true,
  });
  const { getTranslation: t } = useTranslator({ page: 'ordersHub' });
  const canEditOrder = order?.permissions?.includes('edit') ?? false;
  const supplierUsers = useSupplierUserStats([order?.supplierCompanyId]);
  const supplierUserStats =
    supplierUsers.byId.get(order?.supplierCompanyId ?? '') ?? null;
  const supplierNoActiveUser = isSupplierWithoutActiveUser(
    order?.supplierCompanyId,
    supplierUserStats ?? undefined,
  );
  const { data: usersData } = useCompanyUsersQuery(getCompanyId() ?? '', {
    page: 1,
    count: 100,
  });

  const overview = useOrderHubOverview({
    order,
    companyOptions: options,
    onPatch: patch,
    supplierProfile,
    buyerProfile,
    receiverProfile,
    linesCount: linesTable.lines.length,
    activeSection: section === 'lines' ? 'lines' : 'info',
    selectSection: next =>
      void navigate({
        to: '/orders-hub/$id/overview',
        params: { id },
        search: next === 'lines' ? { section: 'lines' } : {},
      }),
    onCompanyEditOpen: () => setPicklistEnabled(true),
    picklistLoading: isPicklistLoading,
    companyUsers: usersData?.data ?? [],
    supplierUserStats,
  });

  if (!order) return null;

  return (
    <>
      <SendToTracingDialog
        open={sendFlow.open}
        onOpenChange={sendFlow.setOpen}
        preflight={sendFlow.preflight}
        scope={sendFlow.scope}
        templateName={sendFlow.templateName}
        isPending={sendFlow.isPending}
        onConfirm={sendFlow.confirm}
        onFixTemplate={sendFlow.goToTemplateSetting}
        suppliers={sendFlow.suppliers}
        suppliersResolved={sendFlow.suppliersResolved}
        t={t}
      />
      <SendToCmmDialog
        open={cmmFlow.open}
        onOpenChange={cmmFlow.setOpen}
        preflight={cmmFlow.preflight}
        scope={cmmFlow.scope}
        isPending={cmmFlow.isPending}
        onConfirm={cmmFlow.confirm}
        onSeeLines={cmmFlow.goToLines}
        skippedLines={cmmFlow.skippedLines}
        t={t}
      />
      <OrderHubOverviewTab
        overview={overview}
        linesTable={linesTable}
        canEdit={canEditOrder}
        renderTracingCell={line => (
          <OrderHubLineTracingCell
            line={line}
            order={order}
            readiness={sendReadiness?.[id]?.lines?.[line.id]?.tracing}
            isTracingEnabled={sendFlow.ctx.isCascadeTracingEnabled}
            isSessionReady={sendFlow.isSessionReady}
            isPending={sendFlow.isSending(order.id)}
            canEdit={canEditOrder}
            supplierNoActiveUser={supplierNoActiveUser}
            onStart={sendFlow.start}
            t={t}
          />
        )}
        renderCmmCell={line => (
          <OrderHubLineCmmCell
            line={line}
            order={order}
            readiness={sendReadiness?.[id]?.lines?.[line.id]?.cmm}
            isCmmEnabled={cmmFlow.ctx.isCmmIntegrationEnabled}
            isLoaded={sendFlow.isSessionReady && cmmFlow.isLoaded}
            isPending={cmmFlow.isSending(order.id)}
            canSend={cmmFlow.canSend(order.permissions)}
            onStart={cmmFlow.start}
            t={t}
          />
        )}
      />
    </>
  );
}
